import { TFunction } from 'i18next';
import React from 'react';
import { useTranslation } from 'react-i18next';

import { LinkStyle } from '@sola/ark-core/atoms/link-style/link-style.js';
import {
  ActionCard,
  ActionCardBody,
  ActionCardDivider,
  ActionCardFooter,
  ActionCardMeta,
  ActionCardSection,
  ActionCardSummary,
} from '@sola/ark-core/molecules/action-card/action-card.js';
import '@sola/locales/formatters.js';
import { GraphQLPledge } from '@sola/schema/dotcom/supporters/schema.generated.js';

import { CommitmentAvatar } from '../../atoms/commitment-avatar/commitment-avatar.js';
import beyondMeasureLogo from '../../images/pledge-beyond-measure.png';
import { CommitmentDetailsSheet } from '../../organisms/commitments-details-sheet/commitment-details-sheet';
import { formatPledgeFooter, formatPledgeName } from '../../utils/pledges.js';

export type PledgeCardProps = {
  pledge: GraphQLPledge;
  isActive?: boolean;
  onActiveChange?: (open: boolean, commitmentId: string) => void;
};

const PledgeIcon = ({ pledgeName }: GraphQLPledge) => {
  switch (pledgeName) {
    case 'Beyond Measure':
      return <CommitmentAvatar responsive imgSrc={beyondMeasureLogo} />;
    default:
      return <CommitmentAvatar responsive />;
  }
};

const useComponentData = (args: PledgeCardProps & { t: TFunction }) => {
  const { pledge, t } = args;

  return {
    ...(pledge.pledgeName && {
      title: formatPledgeName(pledge.pledgeName, t),
    }),
    label: t('commitments.pledges.subTitle'),
    rate: t('core:numbers.currencyCustom', {
      amount: pledge.totalPledgeAmount,
    }),
    footer: formatPledgeFooter(t, pledge.nextInstallmentDate),
  };
};

/**
 * Commitment card, shown for both commitments and giving history items.
 */
export const PledgeCard: React.FC<PledgeCardProps> = (props) => {
  const { t } = useTranslation(['my', 'common']);

  const cd = useComponentData({ t, ...props });
  const { pledge, isActive, onActiveChange } = props;

  return (
    <ActionCard>
      <ActionCardSection>
        <ActionCardBody>
          <ActionCardSummary
            icon={<PledgeIcon {...pledge} />}
            title={cd.title}
            subTitle={cd.label}
          />
          <ActionCardMeta text={cd.rate} />
        </ActionCardBody>
      </ActionCardSection>

      {/* Footer */}
      <ActionCardDivider />
      <ActionCardFooter justify="between">
        <span className="label-moderate label-neutral-mild label-sm md:label-md">
          {cd.footer}
        </span>

        <CommitmentDetailsSheet
          pledge={pledge}
          isOpen={isActive}
          onOpenChange={onActiveChange}
          openTrigger={
            <div className="ml-auto">
              <LinkStyle
                noUnderline
                noPadding
                inline
                variant="primary"
                size="lg"
              >
                <button type="button" className="CommitmentCard__trigger">
                  {t('commitments.detailsSheet.triggerLabel')}
                </button>
              </LinkStyle>
            </div>
          }
        />
      </ActionCardFooter>
    </ActionCard>
  );
};
